import React, { ReactNode } from "react";
import styled, { css } from "styled-components";
import { Asset } from "contentful";
import {
  Bullets,
  BulletsClassName,
  IconName,
  Image,
  Ribbon,
  Theme,
  colors,
  media,
} from "@literati/public-ui-shared-react";
import analytics from "analytics";
import { primaryHeading } from "components/Step/stepStyles";
import {
  DLSAppleAppStoreButton,
  DLSGooglePlayStoreButton,
} from "components/DLS/DLSAppStoreButtons";
import RadialBackground from "components/RadialBackground/RadialBackground";

const subtext =
  "We hope your reader enjoys their first book box. With your feedback,\nwe’ll continually strive to give them the most magical reading experience possible.";

const CARD_FALLBACK_IMAGE_PROPS = {
  src: "//images.ctfassets.net/vqtz4cq2w5ts/34O0pmw57QgfcIoncKHBAc/77645b347d44ed613470980c0cfb53f3/KIDS_Reading_Level_Voyager.png",
  width: 320,
  height: 265,
};

const CLASS_BASE = "onboarding-confirmation";
export const OnboardingConfirmationClassName = {
  BASE: CLASS_BASE,
  HEADING: `${CLASS_BASE}-heading`,
  SUBTEXT: `${CLASS_BASE}-subtext`,
  DELIVERY_DATE: `${CLASS_BASE}-delivery-date`,
  APPLE_BUTTON: `${CLASS_BASE}-apple-button`,
  GOOGLE_BUTTON: `${CLASS_BASE}-google-button`,
  EXPLANATION_TITLE: `${CLASS_BASE}-explanation-title`,
  DISCLAIMER: `${CLASS_BASE}-disclaimer`,
} as const;

const flexColumnCenter = css`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledDiv = styled.div`
  ${flexColumnCenter}
  height: 100%;
  padding: 2rem 1rem;
  background: ${colors.white};
`;

const Heading = styled.h1`
  ${primaryHeading};
  padding: 0 1rem;
  font-size: 2rem;
`;

const Subtext = styled.p`
  margin: 1.5rem 0 0;
  padding: 0 1rem;
  font-size: 0.875rem;
  text-align: center;
  color: ${colors.warmGray4};

  ${media.sm} {
    white-space: pre-wrap;
  }
`;

const StyledRibbon = styled(Ribbon)`
  margin: 2.5rem 0 0;
`;

const DeliveryDate = styled.div`
  margin: 0.75rem;
  font-weight: 600;
  font-size: 1.25rem;
  text-align: center;
`;

const AppStoreButtons = styled.div<{ $hasDeliveryDate: boolean }>`
  display: flex;
  gap: 1rem;
  margin: ${({ $hasDeliveryDate }) =>
    $hasDeliveryDate ? "1.5rem 0 0" : "2.5rem 0 0"};
`;

const AppleButton = styled(DLSAppleAppStoreButton)`
  width: 145px;
  height: 45px;
`;

const GoogleButton = styled(DLSGooglePlayStoreButton)`
  width: 145px;
  height: 45px;
`;

const StyledRadialBackground = styled(RadialBackground)`
  ${flexColumnCenter}
  margin: 2rem 0 0;
`;

const ExplanationText = styled.div`
  z-index: 1;
  ${flexColumnCenter}
  width: 100vw;
`;

const ExplanationTitle = styled.h2`
  margin: 1rem 0 0;
  font-weight: 500;
  font-size: 1.125rem;
  text-align: left;

  ${media.md} {
    text-align: center;
    font-size: 1.125rem;
  }
`;

const StyledBullets = styled(Bullets)`
  padding-left: 3.25rem;

  .${BulletsClassName.BULLET} {
    font-size: 0.875rem;
  }
`;

const Disclaimer = styled.p`
  font-size: 0.75rem;
  text-align: center;
  margin: 0;
  padding: 1rem;
`;

// In order to keep radial gradient static vertically, we add this
// element below it to fill out the remaining background
const ExpandedBackground = styled.div`
  flex: 1 1 auto;
  width: 100vw;
  background: ${colors.periwinkleTint5};
`;

export interface OnboardingConfirmationContentProps {
  className?: string;
  deliveryDate: ReactNode;
  readingLevelImage?: Asset["fields"];
}

export const OnboardingConfirmationContent = ({
  className = "",
  deliveryDate,
  readingLevelImage,
}: OnboardingConfirmationContentProps) => {
  const cls = OnboardingConfirmationClassName;

  const imageProps = readingLevelImage
    ? {
        src: readingLevelImage.file?.url,
        width: readingLevelImage.file?.details?.image?.width,
        height: readingLevelImage.file?.details?.image?.height,
      }
    : CARD_FALLBACK_IMAGE_PROPS;

  const hasDeliveryDate = Boolean(deliveryDate);

  return (
    <Theme>
      <StyledDiv className={`${cls.BASE} ${className}`}>
        <Heading className={cls.HEADING}>
          An exciting new chapter starts now!
        </Heading>
        <Subtext className={cls.SUBTEXT}>{subtext}</Subtext>
        {hasDeliveryDate && (
          <>
            <StyledRibbon
              bgColor="periwinkleWhimsical"
              label="EXPECTED DELIVERY"
              variant="BOTH"
              preset="SM"
            />
            <DeliveryDate className={cls.DELIVERY_DATE}>
              {deliveryDate}
            </DeliveryDate>
          </>
        )}
        <AppStoreButtons $hasDeliveryDate={hasDeliveryDate}>
          <AppleButton
            className={cls.APPLE_BUTTON}
            onClick={() => analytics.trackAmplitudeClickEvent("iOS Store")}
          />
          <GoogleButton
            className={cls.GOOGLE_BUTTON}
            onClick={() => analytics.trackAmplitudeClickEvent("Android Store")}
          />
        </AppStoreButtons>
        <StyledRadialBackground
          bottomColor="periwinkleTint5"
          topColor="white"
          top="460px"
        >
          <Image alt="" contain {...imageProps} />
          <ExplanationText>
            <ExplanationTitle className={cls.EXPLANATION_TITLE}>
              What to expect in your first box
            </ExplanationTitle>
            <StyledBullets
              bgColor="periwinkleTint5"
              iconProps={{
                name: IconName.STAR,
                fill: "tealShade1",
              }}
              items={[
                {
                  textContent: (
                    <span>
                      <strong>5 books</strong> curated just for your reader*
                    </span>
                  ),
                  key: 0,
                },
                {
                  textContent: (
                    <span>
                      <strong>high-quality art print</strong> illustration
                    </span>
                  ),
                  key: 1,
                },
                {
                  textContent: (
                    <span>
                      Stickers and <strong>storytime activities</strong>!
                    </span>
                  ),
                  key: 2,
                },
                {
                  textContent: (
                    <span>Parent guide with reading tips and resources</span>
                  ),
                  key: 3,
                },
              ]}
            />
          </ExplanationText>
          <Disclaimer className={cls.DISCLAIMER}>
            *your box may not include the specific titles within Book Shuffle or
            activities shown in images
          </Disclaimer>
        </StyledRadialBackground>
        <ExpandedBackground />
      </StyledDiv>
    </Theme>
  );
};

export default OnboardingConfirmationContent;
